import React, { useCallback, useEffect } from 'react';
import { 
  Box, 
  Typography, 
  Grid, 
  Button,
  TextField,
  Checkbox,
  FormControlLabel,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions
} from '@mui/material';
import { Add, Edit, Delete } from '@mui/icons-material';
import { Counsellor } from '../../types/counsellor';
import { refreshDocumentUrls } from '../../utils/api';
import { toDateOnlyString } from '../../utils/dateUtils';
import DatePicker from 'react-datepicker';
import "react-datepicker/dist/react-datepicker.css";

interface EducationInfo {
  _id?: string;
  courseName?: string;
  learningInstitution?: string;
  qualificationType?: string;
  hoursCompleted?: string;
  inProgress?: boolean;
  expectedCompletionDate?: string;
  completionDate?: string;
  qualifications?: any[];
  onlineLearningCertificate?: {
    url: string;
    fileName: string;
    originalName: string;
  };
}

interface EducationInformationProps {
  counsellor: Counsellor;
  isEditingEducation?: boolean;
  courseName?: string;
  learningInstitution?: string;
  qualificationType?: string;
  hoursCompleted?: string;
  inProgress?: boolean;
  completionDate?: string;
  expectedCompletionDate?: string;
  additionalQualifications?: any[];
  setCourseName?: (value: string) => void;
  setLearningInstitution?: (value: string) => void;
  setQualificationType?: (value: string) => void;
  setHoursCompleted?: (value: string) => void;
  setInProgress?: (value: boolean) => void;
  setCompletionDate?: (value: string) => void;
  setExpectedCompletionDate?: (value: string) => void;
  setAdditionalQualifications?: (value: any[]) => void;
  handleSaveEducation?: () => void;
  setIsEditingEducation?: (value: boolean) => void;
}

const EducationInformation: React.FC<EducationInformationProps> = ({ 
  counsellor,
  isEditingEducation = false,
  courseName,
  learningInstitution,
  qualificationType,
  hoursCompleted,
  inProgress,
  completionDate,
  expectedCompletionDate,
  additionalQualifications,
  setCourseName,
  setLearningInstitution,
  setQualificationType,
  setHoursCompleted,
  setInProgress,
  setCompletionDate,
  setExpectedCompletionDate,
  setAdditionalQualifications,
  handleSaveEducation,
  setIsEditingEducation
}) => {
  // Use props if provided (editing mode), otherwise use counsellor data (read-only mode)
  const educationData = counsellor.application?.education as EducationInfo | undefined;
  const displayCourseName = courseName !== undefined ? courseName : (educationData?.courseName || '');
  const displayInstitution = learningInstitution !== undefined ? learningInstitution : (educationData?.learningInstitution || '');
  const displayQualificationType = qualificationType !== undefined ? qualificationType : (educationData?.qualificationType || '');
  const displayHoursCompleted = hoursCompleted !== undefined ? hoursCompleted : (educationData?.hoursCompleted || '');
  const displayInProgress = inProgress !== undefined ? inProgress : (educationData?.inProgress || false);
  const displayCompletionDate = completionDate !== undefined ? completionDate : (educationData?.completionDate || '');
  const displayExpectedCompletionDate = expectedCompletionDate !== undefined ? expectedCompletionDate : (educationData?.expectedCompletionDate || '');
  const displayQualifications = additionalQualifications !== undefined ? additionalQualifications : (educationData?.qualifications || []);
  
  // Check if editing is enabled (all required handlers are present)
  const canEdit = !!(setCourseName && setLearningInstitution && handleSaveEducation && setIsEditingEducation);
  const [documentUrls, setDocumentUrls] = React.useState<{
    cv?: string;
  }>({});
  
  // Additional qualification dialog state
  const [qualDialogOpen, setQualDialogOpen] = React.useState(false);
  const [editingQualIndex, setEditingQualIndex] = React.useState<number | null>(null);
  const [qualForm, setQualForm] = React.useState({
    name: '',
    institution: '',
    qualification: '',
    completionDate: '',
    expectedCompletionDate: '',
    grade: '',
    inProgress: false,
    awaitingGrade: false,
    details: ''
  });

  const fetchDocuments = useCallback(async () => {
    try {
      // Use the same refresh endpoint as Documents component
      const refreshedUrls = await refreshDocumentUrls(counsellor._id);
      setDocumentUrls({
        cv: refreshedUrls.cv
      });
    } catch (error) {
      console.error('Error fetching documents:', error);
    }
  }, [counsellor]);

  useEffect(() => {
    fetchDocuments();
  }, [fetchDocuments]);

  const handleDocumentClick = async (e: React.MouseEvent<HTMLElement>, documentType: keyof typeof documentUrls) => {
    e.preventDefault();
    
    try {
      // Refresh document URLs to get fresh presigned URLs
      const refreshedUrls = await refreshDocumentUrls(counsellor._id);
      setDocumentUrls({
        cv: refreshedUrls.cv
      });
      
      // Open the document in a new tab (not download)
      const documentUrl = refreshedUrls[documentType];
      if (documentUrl) {
        window.open(documentUrl, '_blank');
      }
    } catch (error) {
      console.error(`Error refreshing ${documentType} URL:`, error);
    }
  };

  const renderInput = (
    value: string,
    setter: ((value: string) => void) | undefined,
    label: string,
    type: string = 'text',
    placeholder: string = ''
  ) => {
    return isEditingEducation && setter ? (
      <TextField
        type={type}
        value={value || ''}
        onChange={(e) => setter(e.target.value)}
        fullWidth
        placeholder={placeholder}
        variant="outlined"
        size="small"
        sx={{
          '& .MuiOutlinedInput-root': {
            color: 'rgba(255, 255, 255, 0.9)',
            '& fieldset': {
              borderColor: 'rgba(255, 255, 255, 0.2)'
            },
            '&:hover fieldset': {
              borderColor: 'rgba(255, 255, 255, 0.3)'
            },
            '&.Mui-focused fieldset': {
              borderColor: '#3b82f6'
            }
          },
          '& .MuiInputLabel-root': {
            color: 'rgba(255, 255, 255, 0.7)'
          }
        }}
      />
    ) : (
      <Typography variant="body1" className="theme-text-primary">{value || 'Not specified'}</Typography>
    );
  };

  return (
    <Box sx={{ mt: 3 }}>

      <Grid container spacing={3}>
        {/* Education */}
        <Grid item xs={12}>
          <div className="glass-card p-6">
            
            {/* Edit/Save Buttons */}
            {canEdit && (
              <div className="flex justify-between items-center mb-6">
                <div></div>
                <div className="flex gap-4">
                  {isEditingEducation ? (
                  <>
                    <button
                      onClick={() => setIsEditingEducation?.(false)}
                      className="px-4 py-2 text-sm font-bold theme-text-primary bg-white/10 border border-white/20 rounded-md shadow-sm hover:bg-white/20 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-400 min-w-[160px] transition-colors"
                    >
                      Cancel
                    </button>
                    <button
                      onClick={handleSaveEducation}
                      className="px-4 py-2 text-sm font-bold text-white bg-blue-600 border border-transparent rounded-md shadow-sm hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 min-w-[160px] transition-colors"
                    >
                      Save Changes
                    </button>
                  </>
                  ) : (
                    <button
                      onClick={() => setIsEditingEducation?.(true)}
                      className="px-4 py-2 text-sm font-bold text-white bg-blue-600 border border-transparent rounded-md shadow-sm hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 min-w-[160px] transition-colors"
                    >
                      Edit Information
                    </button>
                  )}
                </div>
              </div>
            )}
            
            {/* Course Information */}
            <Box sx={{ mb: 4 }}>
              <Typography variant="subtitle1" className="theme-text-primary" fontWeight="bold" gutterBottom>
                Current Course Details
              </Typography>
              <Grid container spacing={2}>
                <Grid item xs={12} sm={6}>
                  {isEditingEducation ? (
                    <>
                      <Typography variant="subtitle1" fontWeight="bold" gutterBottom className="theme-text-primary">Course Name</Typography>
                      {renderInput(displayCourseName, setCourseName, 'Course Name')}
                    </>
                  ) : (
                    <Typography variant="body1" sx={{ display: 'flex', alignItems: 'center' }}>
                      <Typography variant="subtitle1" className="theme-text-primary" fontWeight="bold" sx={{ mr: 1, minWidth: '180px', flexShrink: 0 }}>Course Name:</Typography>
                      <Typography variant="body1" className="theme-text-primary">{displayCourseName || 'Not specified'}</Typography>
                    </Typography>
                  )}
                </Grid>
                <Grid item xs={12} sm={6}>
                  {isEditingEducation ? (
                    <>
                      <Typography variant="subtitle1" fontWeight="bold" gutterBottom className="theme-text-primary">Institution</Typography>
                      {renderInput(displayInstitution, setLearningInstitution, 'Institution')}
                    </>
                  ) : (
                    <Typography variant="body1" sx={{ display: 'flex', alignItems: 'center' }}>
                      <Typography variant="subtitle1" className="theme-text-primary" fontWeight="bold" sx={{ mr: 1, minWidth: '180px', flexShrink: 0 }}>Institution:</Typography>
                      <Typography variant="body1" className="theme-text-primary">{displayInstitution || 'Not specified'}</Typography>
                    </Typography>
                  )}
                </Grid>
                <Grid item xs={12} sm={6}>
                  {isEditingEducation ? (
                    <>
                      <Typography variant="subtitle1" fontWeight="bold" gutterBottom className="theme-text-primary">Qualification Type</Typography>
                      {renderInput(displayQualificationType, setQualificationType, 'Qualification Type')}
                    </>
                  ) : (
                    <Typography variant="body1" sx={{ display: 'flex', alignItems: 'center' }}>
                      <Typography variant="subtitle1" className="theme-text-primary" fontWeight="bold" sx={{ mr: 1, minWidth: '180px', flexShrink: 0 }}>Qualification Type:</Typography>
                      <Typography variant="body1" className="theme-text-primary">{displayQualificationType || 'Not specified'}</Typography>
                    </Typography>
                  )}
                </Grid>
                <Grid item xs={12} sm={6}>
                  {isEditingEducation ? (
                    <>
                      <Typography variant="subtitle1" fontWeight="bold" gutterBottom className="theme-text-primary">Hours Completed</Typography>
                      {renderInput(displayHoursCompleted, setHoursCompleted, 'Hours Completed', 'number')}
                    </>
                  ) : (
                    <Typography variant="body1" sx={{ display: 'flex', alignItems: 'center' }}>
                      <Typography variant="subtitle1" className="theme-text-primary" fontWeight="bold" sx={{ mr: 1, minWidth: '180px', flexShrink: 0 }}>Hours Completed:</Typography>
                      <Typography variant="body1" className="theme-text-primary">{displayHoursCompleted || 'Not specified'}</Typography>
                    </Typography>
                  )}
                </Grid>
                {isEditingEducation ? (
                  <Grid item xs={12}>
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={displayInProgress}
                          onChange={(e) => setInProgress?.(e.target.checked)}
                          sx={{
                            color: 'rgba(255, 255, 255, 0.7)',
                            '&.Mui-checked': {
                              color: '#3b82f6'
                            }
                          }}
                        />
                      }
                      label={<Typography className="theme-text-primary">Still in progress</Typography>}
                    />
                  </Grid>
                ) : (
                  <Grid item xs={12} sm={6}>
                    <Typography variant="body1" sx={{ display: 'flex', alignItems: 'center' }}>
                      <Typography variant="subtitle1" className="theme-text-primary" fontWeight="bold" sx={{ mr: 1, minWidth: '180px', flexShrink: 0 }}>Status:</Typography>
                      <Typography variant="body1" className="theme-text-primary">{displayInProgress ? 'Still studying' : 'Completed'}</Typography>
                    </Typography>
                  </Grid>
                )}
                
                {displayInProgress ? (
                  displayExpectedCompletionDate && (
                    <Grid item xs={12} sm={6}>
                      {isEditingEducation ? (
                        <>
                          <Typography variant="subtitle1" fontWeight="bold" gutterBottom>Expected Completion Date</Typography>
                          <DatePicker
                            selected={displayExpectedCompletionDate ? new Date(displayExpectedCompletionDate) : null}
                            onChange={(date: Date | null) => setExpectedCompletionDate?.(date ? toDateOnlyString(date) : '')}
                            dateFormat="dd/MM/yyyy"
                            placeholderText="Select date"
                            className="w-full px-3 py-2 bg-white/5 border border-white/20 rounded-md text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
                          />
                        </>
                      ) : (
                        <Typography variant="body1" sx={{ display: 'flex', alignItems: 'center' }}>
                          <Typography variant="subtitle1" className="theme-text-primary" fontWeight="bold" sx={{ mr: 1, minWidth: '180px', flexShrink: 0 }}>Expected Completion:</Typography>
                          <Typography variant="body1" className="theme-text-primary">{displayExpectedCompletionDate ? new Date(displayExpectedCompletionDate).toLocaleDateString() : 'Not specified'}</Typography>
                        </Typography>
                      )}
                    </Grid>
                  )
                ) : (
                  displayCompletionDate && (
                    <Grid item xs={12} sm={6}>
                      {isEditingEducation ? (
                        <>
                          <Typography variant="subtitle1" fontWeight="bold" gutterBottom>Completion Date</Typography>
                          <DatePicker
                            selected={displayCompletionDate ? new Date(displayCompletionDate) : null}
                            onChange={(date: Date | null) => setCompletionDate?.(date ? toDateOnlyString(date) : '')}
                            dateFormat="dd/MM/yyyy"
                            placeholderText="Select date"
                            className="w-full px-3 py-2 bg-white/5 border border-white/20 rounded-md text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
                          />
                        </>
                      ) : (
                        <Typography variant="body1" sx={{ display: 'flex', alignItems: 'center' }}>
                          <Typography variant="subtitle1" className="theme-text-primary" fontWeight="bold" sx={{ mr: 1, minWidth: '180px', flexShrink: 0 }}>Completion Date:</Typography>
                          <Typography variant="body1" className="theme-text-primary">{displayCompletionDate ? new Date(displayCompletionDate).toLocaleDateString() : 'Not specified'}</Typography>
                        </Typography>
                      )}
                    </Grid>
                  )
                )}
              </Grid>
            </Box>
            
            {/* Additional Qualifications */}
            <Box sx={{ mb: 3 }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                <Typography variant="subtitle1" className="theme-text-primary" fontWeight="bold">
                  Additional Qualifications
                </Typography>
                {isEditingEducation && canEdit && (
                  <Button
                    variant="contained"
                    size="small"
                    startIcon={<Add />}
                    onClick={() => {
                      setQualForm({
                        name: '',
                        institution: '',
                        qualification: '',
                        completionDate: '',
                        expectedCompletionDate: '',
                        grade: '',
                        inProgress: false,
                        awaitingGrade: false,
                        details: ''
                      });
                      setEditingQualIndex(null);
                      setQualDialogOpen(true);
                    }}
                    sx={{
                      backgroundColor: '#3b82f6',
                      '&:hover': {
                        backgroundColor: '#2563eb'
                      }
                    }}
                  >
                    Add Qualification
                  </Button>
                )}
              </Box>
              
              {displayQualifications && displayQualifications.length > 0 ? (
                displayQualifications.map((qualification, index) => (
                  <Box key={index} sx={{ mb: 3, p: 2, border: '1px solid rgba(255, 255, 255, 0.1)', borderRadius: 2, position: 'relative' }}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                      <Typography variant="body2" className="theme-text-primary" fontWeight="bold">
                        Qualification {index + 1}
                      </Typography>
                      {isEditingEducation && (
                        <Box>
                          <IconButton
                            size="small"
                            onClick={() => {
                              setQualForm(qualification);
                              setEditingQualIndex(index);
                              setQualDialogOpen(true);
                            }}
                            sx={{ color: '#3b82f6', mr: 1 }}
                          >
                            <Edit fontSize="small" />
                          </IconButton>
                          <IconButton
                            size="small"
                            onClick={() => {
                              const updated = displayQualifications.filter((_, i) => i !== index);
                              setAdditionalQualifications?.(updated);
                            }}
                            sx={{ color: '#ef4444' }}
                          >
                            <Delete fontSize="small" />
                          </IconButton>
                        </Box>
                      )}
                    </Box>
                    <Grid container spacing={2}>
                      <Grid item xs={12} sm={6}>
                        <Typography variant="body1" sx={{ display: 'flex', alignItems: 'center' }}>
                          <Typography variant="subtitle1" className="theme-text-primary" fontWeight="bold" sx={{ mr: 1, minWidth: '180px', flexShrink: 0 }}>Course Name:</Typography>
                          <Typography variant="body1" className="theme-text-primary">{qualification.name || qualification.qualification || 'Not specified'}</Typography>
                        </Typography>
                      </Grid>
                      <Grid item xs={12} sm={6}>
                        <Typography variant="body1" sx={{ display: 'flex', alignItems: 'center' }}>
                          <Typography variant="subtitle1" className="theme-text-primary" fontWeight="bold" sx={{ mr: 1, minWidth: '180px', flexShrink: 0 }}>Institution:</Typography>
                          <Typography variant="body1" className="theme-text-primary">{qualification.institution || 'Not specified'}</Typography>
                        </Typography>
                      </Grid>
                      <Grid item xs={12} sm={6}>
                        <Typography variant="body1" sx={{ display: 'flex', alignItems: 'center' }}>
                          <Typography variant="subtitle1" className="theme-text-primary" fontWeight="bold" sx={{ mr: 1, minWidth: '180px', flexShrink: 0 }}>Qualification Type:</Typography>
                          <Typography variant="body1" className="theme-text-primary">{qualification.qualification || 'Not specified'}</Typography>
                        </Typography>
                      </Grid>
                      {qualification.completionDate && (
                        <Grid item xs={12} sm={6}>
                          <Typography variant="body1" sx={{ display: 'flex', alignItems: 'center' }}>
                            <Typography variant="subtitle1" className="theme-text-primary" fontWeight="bold" sx={{ mr: 1, minWidth: '180px', flexShrink: 0 }}>Completion Date:</Typography>
                            <Typography variant="body1" className="theme-text-primary">{new Date(qualification.completionDate).toLocaleDateString()}</Typography>
                          </Typography>
                        </Grid>
                      )}
                      {qualification.expectedCompletionDate && qualification.inProgress && (
                        <Grid item xs={12} sm={6}>
                          <Typography variant="body1" sx={{ display: 'flex', alignItems: 'center' }}>
                            <Typography variant="subtitle1" className="theme-text-primary" fontWeight="bold" sx={{ mr: 1, minWidth: '180px', flexShrink: 0 }}>Expected Completion:</Typography>
                            <Typography variant="body1" className="theme-text-primary">{new Date(qualification.expectedCompletionDate).toLocaleDateString()}</Typography>
                          </Typography>
                        </Grid>
                      )}
                      {qualification.grade && (
                        <Grid item xs={12} sm={6}>
                          <Typography variant="body1" sx={{ display: 'flex', alignItems: 'center' }}>
                            <Typography variant="subtitle1" className="theme-text-primary" fontWeight="bold" sx={{ mr: 1, minWidth: '180px', flexShrink: 0 }}>Grade:</Typography>
                            <Typography variant="body1" className="theme-text-primary">{qualification.grade}</Typography>
                          </Typography>
                        </Grid>
                      )}
                      <Grid item xs={12} sm={6}>
                        <Typography variant="body1" sx={{ display: 'flex', alignItems: 'center' }}>
                          <Typography variant="subtitle1" className="theme-text-primary" fontWeight="bold" sx={{ mr: 1, minWidth: '180px', flexShrink: 0 }}>Status:</Typography>
                          <Typography variant="body1" className="theme-text-primary">
                            {qualification.awaitingGrade ? 'Awaiting grade' :
                             qualification.inProgress ? 'In progress' :
                             qualification.completionDate ? 'Completed' : 'Not specified'}
                          </Typography>
                        </Typography>
                      </Grid>
                      {qualification.details && (
                        <Grid item xs={12}>
                          <Typography variant="body1" sx={{ display: 'flex', alignItems: 'flex-start' }}>
                            <Typography variant="subtitle1" className="theme-text-primary" fontWeight="bold" sx={{ mr: 1, minWidth: '180px', flexShrink: 0 }}>Details:</Typography>
                            <Typography variant="body1" className="theme-text-primary" sx={{ flex: 1 }}>{qualification.details}</Typography>
                          </Typography>
                        </Grid>
                      )}
                    </Grid>
                  </Box>
                ))
              ) : (
                <Typography className="theme-text-secondary" sx={{ textAlign: 'center', py: 2 }}>
                  No additional qualifications added
                </Typography>
              )}
            </Box>

          </div>
        </Grid>

        {/* Documents */}
        <Grid item xs={12}>
          <div className="glass-card p-6">
            <Grid container spacing={3}>
              <Grid item xs={12} md={6}>
                <Typography variant="body1" sx={{ display: 'flex', alignItems: 'center' }}>
                  <Typography variant="subtitle1" className="theme-text-primary" fontWeight="bold" sx={{ mr: 1, minWidth: '180px', flexShrink: 0 }}>CV:</Typography>
                  {counsellor.application?.counselling?.cv ? (
                    <Button 
                      variant="text"
                      onClick={(e) => handleDocumentClick(e, 'cv')}
                      sx={{ 
                        p: 0, 
                        textTransform: 'none', 
                        textAlign: 'left',
                        color: 'rgba(255, 255, 255, 0.9)',
                        textDecoration: 'underline',
                        '&:hover': {
                          backgroundColor: 'transparent',
                          textDecoration: 'underline'
                        }
                      }}
                    >
                      {counsellor.application.counselling.cv.originalName || 'CV Document'}
                    </Button>
                  ) : (
                    <Typography className="theme-text-secondary">Not provided</Typography>
                  )}
                </Typography>
              </Grid>
              
              <Grid item xs={12}>
                <Typography variant="body2" className="theme-text-secondary" sx={{ mt: 4 }}>
                  Note: Legal documents (DBS certificate, insurance certificate and telephone certificate) will be collected separately after the interview process.
                </Typography>
              </Grid>
            </Grid>
          </div>
        </Grid>
      </Grid>
      
      {/* Add/Edit Qualification Dialog */}
      <Dialog
        open={qualDialogOpen}
        onClose={() => setQualDialogOpen(false)}
        maxWidth="md"
        fullWidth
        PaperProps={{
          sx: {
            backgroundColor: '#1e293b',
            backgroundImage: 'none',
            color: 'rgba(255, 255, 255, 0.9)'
          }
        }}
      >
        <DialogTitle sx={{ borderBottom: '1px solid rgba(255, 255, 255, 0.1)' }}>
          {editingQualIndex !== null ? 'Edit Qualification' : 'Add Qualification'}
        </DialogTitle>
        <DialogContent sx={{ mt: 2 }}>
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6}>
              <Typography variant="subtitle2" sx={{ mb: 1 }}>Course Name *</Typography>
              <TextField
                fullWidth
                value={qualForm.name}
                onChange={(e) => setQualForm({ ...qualForm, name: e.target.value })}
                size="small"
                sx={{
                  '& .MuiOutlinedInput-root': {
                    color: 'rgba(255, 255, 255, 0.9)',
                    '& fieldset': { borderColor: 'rgba(255, 255, 255, 0.2)' },
                    '&:hover fieldset': { borderColor: 'rgba(255, 255, 255, 0.3)' },
                    '&.Mui-focused fieldset': { borderColor: '#3b82f6' }
                  }
                }}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <Typography variant="subtitle2" sx={{ mb: 1 }}>Institution *</Typography>
              <TextField
                fullWidth
                value={qualForm.institution}
                onChange={(e) => setQualForm({ ...qualForm, institution: e.target.value })}
                size="small"
                sx={{
                  '& .MuiOutlinedInput-root': {
                    color: 'rgba(255, 255, 255, 0.9)',
                    '& fieldset': { borderColor: 'rgba(255, 255, 255, 0.2)' },
                    '&:hover fieldset': { borderColor: 'rgba(255, 255, 255, 0.3)' },
                    '&.Mui-focused fieldset': { borderColor: '#3b82f6' }
                  }
                }}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <Typography variant="subtitle2" sx={{ mb: 1 }}>Qualification Type</Typography>
              <TextField
                fullWidth
                value={qualForm.qualification}
                onChange={(e) => setQualForm({ ...qualForm, qualification: e.target.value })}
                size="small"
                sx={{
                  '& .MuiOutlinedInput-root': {
                    color: 'rgba(255, 255, 255, 0.9)',
                    '& fieldset': { borderColor: 'rgba(255, 255, 255, 0.2)' },
                    '&:hover fieldset': { borderColor: 'rgba(255, 255, 255, 0.3)' },
                    '&.Mui-focused fieldset': { borderColor: '#3b82f6' }
                  }
                }}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <Typography variant="subtitle2" sx={{ mb: 1 }}>Grade</Typography>
              <TextField
                fullWidth
                value={qualForm.grade}
                onChange={(e) => setQualForm({ ...qualForm, grade: e.target.value })}
                size="small"
                sx={{
                  '& .MuiOutlinedInput-root': {
                    color: 'rgba(255, 255, 255, 0.9)',
                    '& fieldset': { borderColor: 'rgba(255, 255, 255, 0.2)' },
                    '&:hover fieldset': { borderColor: 'rgba(255, 255, 255, 0.3)' },
                    '&.Mui-focused fieldset': { borderColor: '#3b82f6' }
                  }
                }}
              />
            </Grid>
            <Grid item xs={12}>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={qualForm.inProgress}
                    onChange={(e) => setQualForm({ ...qualForm, inProgress: e.target.checked })}
                    sx={{
                      color: 'rgba(255, 255, 255, 0.7)',
                      '&.Mui-checked': { color: '#3b82f6' }
                    }}
                  />
                }
                label="In Progress"
              />
              <FormControlLabel
                control={
                  <Checkbox
                    checked={qualForm.awaitingGrade}
                    onChange={(e) => setQualForm({ ...qualForm, awaitingGrade: e.target.checked })}
                    sx={{
                      color: 'rgba(255, 255, 255, 0.7)',
                      '&.Mui-checked': { color: '#3b82f6' }
                    }}
                  />
                }
                label="Awaiting Grade"
              />
            </Grid>
            {qualForm.inProgress ? (
              <Grid item xs={12} sm={6}>
                <Typography variant="subtitle2" sx={{ mb: 1 }}>Expected Completion Date</Typography>
                <DatePicker
                  selected={qualForm.expectedCompletionDate ? new Date(qualForm.expectedCompletionDate) : null}
                  onChange={(date: Date | null) => setQualForm({ ...qualForm, expectedCompletionDate: date ? toDateOnlyString(date) : '' })}
                  dateFormat="dd/MM/yyyy"
                  placeholderText="Select date"
                  className="w-full px-3 py-2 bg-white/5 border border-white/20 rounded-md text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </Grid>
            ) : (
              <Grid item xs={12} sm={6}>
                <Typography variant="subtitle2" sx={{ mb: 1 }}>Completion Date</Typography>
                <DatePicker
                  selected={qualForm.completionDate ? new Date(qualForm.completionDate) : null}
                  onChange={(date: Date | null) => setQualForm({ ...qualForm, completionDate: date ? toDateOnlyString(date) : '' })}
                  dateFormat="dd/MM/yyyy"
                  placeholderText="Select date"
                  className="w-full px-3 py-2 bg-white/5 border border-white/20 rounded-md text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </Grid>
            )}
            <Grid item xs={12}>
              <Typography variant="subtitle2" sx={{ mb: 1 }}>Additional Details</Typography>
              <TextField
                fullWidth
                multiline
                rows={3}
                value={qualForm.details}
                onChange={(e) => setQualForm({ ...qualForm, details: e.target.value })}
                size="small"
                sx={{
                  '& .MuiOutlinedInput-root': {
                    color: 'rgba(255, 255, 255, 0.9)',
                    '& fieldset': { borderColor: 'rgba(255, 255, 255, 0.2)' },
                    '&:hover fieldset': { borderColor: 'rgba(255, 255, 255, 0.3)' },
                    '&.Mui-focused fieldset': { borderColor: '#3b82f6' }
                  }
                }}
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions sx={{ borderTop: '1px solid rgba(255, 255, 255, 0.1)', p: 2 }}>
          <Button
            onClick={() => setQualDialogOpen(false)}
            sx={{ color: 'rgba(255, 255, 255, 0.7)' }}
          >
            Cancel
          </Button>
          <Button
            onClick={() => {
              if (!qualForm.name || !qualForm.institution) {
                alert('Please fill in required fields (Course Name and Institution)');
                return;
              }
              
              if (editingQualIndex !== null) {
                // Edit existing
                const updated = [...displayQualifications];
                updated[editingQualIndex] = qualForm;
                setAdditionalQualifications?.(updated);
              } else {
                // Add new
                setAdditionalQualifications?.([...displayQualifications, qualForm]);
              }
              
              setQualDialogOpen(false);
            }}
            variant="contained"
            sx={{
              backgroundColor: '#3b82f6',
              '&:hover': { backgroundColor: '#2563eb' }
            }}
          >
            {editingQualIndex !== null ? 'Update' : 'Add'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default EducationInformation;
